'use client';
import {
  Box,
  Container,
  Flex,
  Heading,
  Show,
  SimpleGrid,
} from '@chakra-ui/react';
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import FleetRangeCard from '@/app/_components/fleet-range-card/FleetRangeCard';
import { ReactComponent as ArrowBackwardCircle } from '@/app/_icons/ArrowBackwardCircleOutline.svg';
import { ReactComponent as ArrowForwardCircle } from '@/app/_icons/ArrowForwardCircleOutline.svg';
import type { BodyTypesGrouping } from '@/types/home/home.types';
import type { NewCarListing } from '@/types/new-cars/new-cars.types';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import StylesCorporate from './CorporateFleet.module.scss';
import Styles from './FleetSolutionsTabs.module.scss';

const CorporateFleet = ({
  data: { blocks },
  fleetSpecials,
  bodyTypeConfig,
}: {
  data: { blocks: { title: string; text: string }[] };
  fleetSpecials: NewCarListing[];
  bodyTypeConfig: BodyTypesGrouping[];
}) => {
  const swiperRef: RefObject<SwiperRef> = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [currentCars, setCurrentCars] = useState<NewCarListing[]>(
    fleetSpecials || [],
  );
  const [currentSelectedBodyType, setCurrentSelectedBodyType] = useState<
    string[]
  >([]);

  useEffect(() => {
    const currentFilteredCars =
      fleetSpecials?.filter((car) => {
        if (currentSelectedBodyType.length === 0) {
          return true;
        }

        const bodyTypeValue = car?.body_type?.value;

        return bodyTypeValue
          ? currentSelectedBodyType.includes(bodyTypeValue)
          : false;
      }) || [];

    setCurrentCars([...currentFilteredCars]);
  }, [currentSelectedBodyType]);

  const handleBodyTypeSelection = (selectedBodyTypes: string[]) => {
    setCurrentSelectedBodyType(selectedBodyTypes);
  };

  const goNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const goPrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const CustomNavigation = () => {
    return (
      <div className={StylesCorporate.corporateFleetNavigation}>
        <button onClick={goPrev} className={StylesCorporate.corporateFleetPrev}>
          <ArrowBackwardCircle />
        </button>
        <button onClick={goNext} className={StylesCorporate.corporateFleetNext}>
          <ArrowForwardCircle />
        </button>
      </div>
    );
  };

  return (
    <>
      <Container
        className={Styles.fleetTabPanelVerticalContent}
        maxW={1280}
        w={'100%'}
        marginX={'auto'}
        marginBottom={'56px'}
        paddingTop={{ md: 14 }}
      >
        <Swiper
          ref={swiperRef}
          className={StylesCorporate.corporateFleetSwiper}
          slidesPerView={3}
          spaceBetween={56}
          modules={[Pagination, Navigation]}
          pagination={{
            el: StylesCorporate.corporateFleetSwiperControlsSwiperPagination,
            type: 'fraction',
            formatFractionCurrent: function (number) {
              return number < 10 ? '0' + number : number.toString();
            },
          }}
          navigation={{
            nextEl: StylesCorporate.corporateFleetNext,
            prevEl: StylesCorporate.corporateFleetPrev,
          }}
          onSlideChange={(swiper) => {
            setCurrentPage(swiper.realIndex + 1);
          }}
          onSwiper={(swiper) => {
            setTotalPages(swiper.slides.length);
          }}
          breakpoints={{
            0: {
              slidesPerView: 1,
            },
            576: {
              slidesPerView: 1,
            },
            768: {
              slidesPerView: 2,
            },
            1024: {
              slidesPerView: 3,
            },
          }}
        >
          {blocks?.map((item) => {
            return (
              <SwiperSlide
                key={item?.title}
                className={StylesCorporate.swiperSlide}
              >
                <Box
                  className={Styles.fleetDetails}
                  paddingX={{ base: 8, xl: 0 }}
                >
                  <Box className={Styles.headingElement}>
                    <Heading
                      as="h4"
                      className={Styles.titleMd}
                      marginBottom={{ base: 4, md: 5 }}
                    >
                      {item?.title}
                    </Heading>
                    <Box
                      className={Styles.titleText}
                      dangerouslySetInnerHTML={{ __html: item?.text }}
                    ></Box>
                  </Box>
                </Box>
              </SwiperSlide>
            );
          })}
        </Swiper>
        <Show breakpoint="(max-width: 1023px)">
          <Box className={StylesCorporate.corporateFleetSwiperControls} mt={4}>
            <Box
              className={
                StylesCorporate.corporateFleetSwiperControlsSwiperPagination
              }
            >
              <span className="current-page">{currentPage}</span>
              <span className="separator">/</span>
              <span className="total-pages">{totalPages}</span>
            </Box>
            <CustomNavigation />
          </Box>
        </Show>
      </Container>
      <Box
        className={StylesCorporate.corporateFleetRangeWrapper}
        pt={'56px'}
        pb={'100px'}
      >
        <Container
          maxW="100%"
          className={StylesCorporate.corporateFleetRangeHeader}
          textAlign={'center'}
          marginBottom={{ base: 10, sm: 20 }}
          px={'0!important'}
          id="range"
        >
          <Box
            className={StylesCorporate.headingElement}
            mb={'8!important'}
            px={'0!important'}
          >
            <Heading
              as="h2"
              className={StylesCorporate.titleLg}
              color={'white'}
              marginBottom={6}
            >
              Corporate Fleet Range
            </Heading>
          </Box>
          <Box className={StylesCorporate.corporateFleetStickyNav}>
            <Flex
              justifyContent={{ base: 'start', sm: 'center' }}
              flexWrap={'nowrap'}
              overflowX={'auto'}
              columnGap={{ base: '2', sm: '4' }}
              paddingLeft={{ base: 0 }}
            >
              {bodyTypeConfig?.map((grouping, index) => {
                const isActive = grouping?.body_types.some((value) =>
                  currentSelectedBodyType.includes(value),
                );

                return (
                  <ButtonCustom
                    key={index}
                    btnText={grouping.label}
                    className="btnGreyOutlineDark"
                    moduleClass={StylesCorporate.outlineWhite}
                    extraClass={
                      isActive ||
                      (index === 0 && currentSelectedBodyType.length === 0)
                        ? StylesCorporate.active
                        : ''
                    }
                    onClick={() =>
                      handleBodyTypeSelection(grouping?.body_types)
                    }
                  />
                );
              })}
            </Flex>
          </Box>
        </Container>
        <SimpleGrid
          className={Styles.vehicleList}
          columns={[1, 1, 2, 3]}
          spacingX={[10]}
          spacingY={[20]}
          paddingX={{ base: '6' }}
          justifyContent={'center'}
          alignContent={'center'}
          maxW={1280}
          w={'100%'}
          marginX={'auto'}
        >
          {currentCars?.map((fleetCar) => (
            <FleetRangeCard key={fleetCar.id} fleetCar={fleetCar} />
          ))}
        </SimpleGrid>
      </Box>
    </>
  );
};

export default CorporateFleet;
